import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable, of, Subject, throwError} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {AdvFormService} from '../../pages/create-adv/services/adv-form.service';
import {ApiUrl} from '../../shared/models/apiurl.models';
import {QuestionBase} from '../../pages/create-adv/models/question-base';
import {selectOptionData} from '../../pages/create-adv/dynamic-form-question/dynamic-form-question.component';

interface RelatedDescriptionOption {
  label: string;
  title: string;
  details: string[];
  payload: selectOptionData;
  source: any;
}

interface DescriptionSelection {
  key: string;
  producer: string | null;
  model: string | null;
  year: string | null;
  force: boolean;
}

@Component({
  selector: 'app-select-related-description',
  templateUrl: './select-related-description.component.html',
  styleUrls: ['./select-related-description.component.css'],
  standalone: false
})
export class SelectRelatedDescriptionComponent implements OnInit, OnDestroy {

  private static readonly resultCache = new Map<string, any[]>();

  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiKey.getJWTToken()
    })
  };

  private readonly destroy$ = new Subject<void>();
  private readonly refresh$ = new Subject<boolean>();
  private readonly parentKeys = new Set(['producerDescription', 'mainModelDescription', 'year']);

  @Input() questionBase!: QuestionBase<string>;
  @Input() questionKey!: any;

  descriptionList: RelatedDescriptionOption[] = [];
  isLoading = false;
  loadError = false;
  hasRequiredSelections = false;

  constructor(
    private advFormService: AdvFormService,
    private apiKey: ApiUrl,
    private http: HttpClient,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.refresh$.pipe(
      // Selecting a parent can clear and repopulate several related fields.
      // Let that cascade settle before deciding whether a request is needed.
      debounceTime(140),
      map(force => this.getCurrentSelection(force)),
      distinctUntilChanged((previous, current) =>
        !current.force && previous.key === current.key
      ),
      switchMap(selection => this.fetchDescriptions(selection)),
      takeUntil(this.destroy$)
    ).subscribe(results => {
      this.descriptionList = this.buildUniqueDescriptions(results || []);
    });

    this.advFormService.getQOClickEvent()
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (!event) return;

        if (event.index === this.questionKey && this.advFormService.getQuestion(this.questionKey) == null) {
          this.descriptionList = [];
        }

        if (this.parentKeys.has(event.index)) {
          this.refresh$.next(false);
        }
      });

    // Supports edit flows where producer/model/year were populated before
    // this component was created and therefore emitted no new event.
    this.refresh$.next(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.refresh$.complete();
  }

  private getCurrentSelection(force: boolean): DescriptionSelection {
    const producer = this.advFormService.getQuestion('producerDescription');
    const model = this.advFormService.getQuestion('mainModelDescription');
    const year = this.advFormService.getQuestion('year');
    const producerValue = this.getAnswerValue(producer);
    const modelValue = this.getAnswerValue(model);
    const yearValue = this.getAnswerValue(year);

    if (producerValue == null || modelValue == null || yearValue == null) {
      return {
        key: '__incomplete__',
        producer: null,
        model: null,
        year: null,
        force
      };
    }

    return {
      key: JSON.stringify([producerValue, modelValue, yearValue]),
      producer: producerValue,
      model: modelValue,
      year: yearValue,
      force
    };
  }

  private getAnswerValue(answer: any): string | null {
    const value = answer?.value?.value ?? answer?.value ?? null;
    if (value == null || value === '') return null;
    return String(value);
  }

  private fetchDescriptions(selection: DescriptionSelection): Observable<any[]> {
    this.hasRequiredSelections = selection.producer != null &&
      selection.model != null && selection.year != null;
    this.loadError = false;

    if (!this.hasRequiredSelections) {
      this.isLoading = false;
      return of([]);
    }

    const cachedResults = SelectRelatedDescriptionComponent.resultCache.get(selection.key);
    if (!selection.force && cachedResults) {
      this.isLoading = false;
      return of(cachedResults);
    }

    this.isLoading = true;
    return this.getDescription(
      selection.producer!,
      selection.model!,
      selection.year!
    ).pipe(
      tap(results => {
        SelectRelatedDescriptionComponent.resultCache.set(selection.key, results || []);
      }),
      catchError(() => {
        this.loadError = true;
        return of([]);
      }),
      finalize(() => this.isLoading = false)
    );
  }

  private buildUniqueDescriptions(carDetails: any[]): RelatedDescriptionOption[] {
    const uniqueLabels = new Set<string>();
    const uniqueOptions: RelatedDescriptionOption[] = [];

    for (const source of carDetails) {
      const label = this.translate.instant(
        'sale4you.label.licenseCodeDes.header.2',
        source?.label
      ).toString().trim();
      const uniqueKey = label.replace(/\s+/g, ' ').toLocaleLowerCase();

      if (!uniqueKey || uniqueLabels.has(uniqueKey)) continue;
      uniqueLabels.add(uniqueKey);

      const labelLines = label
        .split(/\r?\n/)
        .map(line => line.replace(/\s+/g, ' ').trim())
        .filter(Boolean);

      uniqueOptions.push({
        label,
        title: labelLines[0] || label,
        details: labelLines.slice(1),
        source,
        payload: {
          id: source?.id,
          label,
          name: 'description',
          value: {value: label, name: 'description'},
          selected: false
        }
      });
    }

    return uniqueOptions;
  }

  isSelected(option: RelatedDescriptionOption): boolean {
    return this.advFormService.getQuestion(this.questionKey)?.label === option.label;
  }

  selectDescription(option: RelatedDescriptionOption): void {
    const engineCapacity = option.source?.label?.engineCapacity ?? option.source?.engineCapacity;
    if (engineCapacity != null) {
      this.advFormService.addQuestion('engineCapacity', engineCapacity);
    }
    this.advFormService.addQuestion(this.questionKey, option.payload);
  }

  retry(): void {
    this.refresh$.next(true);
  }

  private handleObserverError(error: any): Observable<any> {
    return throwError(() => error.error || error);
  }

  getDescription(producer: string, mainModel: string, year: string): Observable<any> {
    return this.http.get(this.apiKey.getCarDescription(producer, mainModel, year), this.options)
      .pipe(catchError(error => this.handleObserverError(error)));
  }
}
